import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Users, Activity, Smartphone, RefreshCw, CheckCircle, XCircle, Wifi, WifiOff, AlertCircle, Pencil, ShieldCheck, ShieldAlert, Trash2 } from 'lucide-react';
import StatusBadge from '../../components/common/StatusBadge';
import Tabs, { type Tab } from '../../components/common/Tabs';
import DataTable from '../../components/common/DataTable';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import type { Column } from '../../components/common/DataTable';
import ZtnaChip from '../../components/access/ZtnaChip';
import UserSignInPolicyCard from '../../components/access/UserSignInPolicyCard';
import UserSignInRouteLine from '../../components/access/UserSignInRouteLine';
import { DeviceDetailModal, RevokeDeviceModal } from '../../components/access/DeviceModals';
import { deviceStatusLabel, fmtDate, postureFacts } from '../../utils/accessDevice';
import accessService, { type AccessDevice, type DeviceQuota } from '../../services/access.service';
import vpnUserService, { type VPNUser } from '../../services/vpnUser.service';
import { appDeviceService, activityService } from '../../services';
import type { AppDevice, DeviceGroup } from '../../services/appDevice.service';
import type { ActivityRecord } from '../../services/activity.service';
import { formatDate, type DateFormatSettings } from '../../utils/formatDate';
import { useSettingsStore } from '../../stores/settingsStore';
import { useToast } from '../../components/common/ToastContainer';
import api from '../../services/api';

/** An error's message, or the fallback (what `err.message || fallback` did, typed). */
const errText = (err: unknown, fallback: string): string =>
  err instanceof Error && err.message ? err.message : fallback;

/** An axios-style error's `response.data.message`, when there is one. */
const responseMessage = (err: unknown): string | undefined => {
  const data = (err as { response?: { data?: { message?: unknown } } } | null)?.response?.data;
  return typeof data?.message === 'string' && data.message ? data.message : undefined;
};

// --- Tab components defined outside the parent to avoid remount on re-render ---

interface OverviewTabProps {
  user: VPNUser;
  dateSettings: DateFormatSettings;
  onRefreshUser: () => Promise<void>;
}

const OverviewTab = ({ user, dateSettings, onRefreshUser }: OverviewTabProps) => {
  const toast = useToast();
  const [editingMaxDevices, setEditingMaxDevices] = useState(false);
  const [maxDevicesValue, setMaxDevicesValue] = useState(user.maxDevices);
  const [saving, setSaving] = useState(false);

  // Device-group inline editor
  const [editingDeviceGroup, setEditingDeviceGroup] = useState(false);
  const [deviceGroups, setDeviceGroups] = useState<DeviceGroup[]>([]);
  const [deviceGroupsLoading, setDeviceGroupsLoading] = useState(false);
  const [deviceGroupValue, setDeviceGroupValue] = useState<string>(user.deviceGroupId || '');
  const [savingDeviceGroup, setSavingDeviceGroup] = useState(false);

  // Sync local value if parent refreshes
  useEffect(() => {
    setMaxDevicesValue(user.maxDevices);
  }, [user.maxDevices]);

  useEffect(() => {
    setDeviceGroupValue(user.deviceGroupId || '');
  }, [user.deviceGroupId]);

  const handleStartEditDeviceGroup = async () => {
    setEditingDeviceGroup(true);
    if (deviceGroups.length === 0) {
      try {
        setDeviceGroupsLoading(true);
        const res = await appDeviceService.listDeviceGroups({ limit: 100 });
        setDeviceGroups(res.items || []);
      } catch (err: unknown) {
        toast.error('Load Failed', errText(err, 'Failed to load device groups'));
      } finally {
        setDeviceGroupsLoading(false);
      }
    }
  };

  const handleSaveDeviceGroup = async () => {
    if (!deviceGroupValue) {
      toast.error('Validation Error', 'Please select a device group');
      return;
    }
    if (deviceGroupValue === user.deviceGroupId) {
      setEditingDeviceGroup(false);
      return;
    }
    try {
      setSavingDeviceGroup(true);
      const result = await vpnUserService.moveDeviceGroup(user.id, deviceGroupValue);
      await onRefreshUser();
      setEditingDeviceGroup(false);
      toast.success(
        'Device Group Updated',
        `Moved to ${result.deviceGroupName} — ${result.movedDevices} device(s) re-homed; affected devices must reconnect`
      );
    } catch (err: unknown) {
      toast.error('Update Failed', errText(err, 'Failed to move device group'));
    } finally {
      setSavingDeviceGroup(false);
    }
  };

  const handleCancelEditDeviceGroup = () => {
    setDeviceGroupValue(user.deviceGroupId || '');
    setEditingDeviceGroup(false);
  };

  const handleSaveMaxDevices = async () => {
    if (maxDevicesValue < 0 || maxDevicesValue > 1000) {
      toast.error('Validation Error', 'Max devices must be between 0 (unlimited) and 1000');
      return;
    }
    try {
      setSaving(true);
      await vpnUserService.update(user.id, { maxDevices: maxDevicesValue });
      await onRefreshUser();
      setEditingMaxDevices(false);
      toast.success('Updated', 'Max devices updated successfully');
    } catch (err: unknown) {
      toast.error('Update Failed', errText(err, 'Failed to update max devices'));
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setMaxDevicesValue(user.maxDevices);
    setEditingMaxDevices(false);
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <div className="card">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Profile Information</h3>
        <div className="space-y-3">
          <div>
            <label className="text-sm font-medium text-gray-500">Email</label>
            <p className="text-gray-900">{user.email}</p>
          </div>
          {/* Sign-in routing by email domain (VSNP-1629, doc 28d §10.1) */}
          <div>
            <label className="text-sm font-medium text-gray-500">Sign-in</label>
            <UserSignInRouteLine route={user.signInRoute} />
          </div>
          <div>
            <label className="text-sm font-medium text-gray-500">Display Name</label>
            <p className="text-gray-900">{user.displayName || 'N/A'}</p>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-500">Status</label>
            <div className="mt-1">
              <StatusBadge status={user.status === 0 ? 'Active' : 'Disabled'} />
            </div>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-500">Tenant</label>
            <p className="text-gray-900">{user.tenantName || user.tenantId}</p>
          </div>
        </div>
      </div>

      <div className="space-y-4">
        <div className="card">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Device & Connection</h3>
          <div className="space-y-3">
            <div>
              <label className="text-sm font-medium text-gray-500">Device Group</label>
              {editingDeviceGroup ? (
                <div className="flex items-center space-x-2 mt-1">
                  <select
                    value={deviceGroupValue}
                    onChange={(e) => setDeviceGroupValue(e.target.value)}
                    className="input-base flex-1 min-w-0"
                    disabled={deviceGroupsLoading || savingDeviceGroup}
                    autoFocus
                  >
                    <option value="">Select a device group…</option>
                    {deviceGroups.map((g) => (
                      <option key={g.id} value={g.id}>{g.name}</option>
                    ))}
                  </select>
                  <button
                    onClick={handleSaveDeviceGroup}
                    disabled={savingDeviceGroup || deviceGroupsLoading}
                    className="btn-primary text-sm px-3 py-1 disabled:opacity-50"
                  >
                    {savingDeviceGroup ? 'Saving…' : 'Save'}
                  </button>
                  <button
                    onClick={handleCancelEditDeviceGroup}
                    disabled={savingDeviceGroup}
                    className="btn-secondary text-sm px-3 py-1"
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <div className="flex items-center space-x-2 mt-0.5">
                  <p className="text-gray-900">{user.deviceGroupName || 'Ungrouped'}</p>
                  <button
                    onClick={handleStartEditDeviceGroup}
                    className="text-xs px-2 py-0.5 rounded bg-gray-50 text-gray-700 hover:bg-gray-100 transition-colors"
                    title="Change device group"
                  >
                    Change
                  </button>
                </div>
              )}
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">Max Devices</label>
              {editingMaxDevices ? (
                <div className="flex items-center space-x-2 mt-1">
                  <input
                    type="number"
                    min={0}
                    max={1000}
                    value={maxDevicesValue}
                    onChange={(e) => setMaxDevicesValue(parseInt(e.target.value, 10) || 0)}
                    className="input-base w-24"
                    autoFocus
                  />
                  <span className="text-xs text-gray-400">0 = Unlimited · applies to VPN app devices and Access (ZTNA) devices, each counted separately</span>
                  <button
                    onClick={handleSaveMaxDevices}
                    disabled={saving}
                    className="btn-primary text-sm px-3 py-1 disabled:opacity-50"
                  >
                    {saving ? 'Saving…' : 'Save'}
                  </button>
                  <button
                    onClick={handleCancelEdit}
                    disabled={saving}
                    className="btn-secondary text-sm px-3 py-1"
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <div className="flex items-center space-x-2 mt-0.5">
                  <p className="text-gray-900">{user.maxDevices === 0 ? 'Unlimited' : user.maxDevices}</p>
                  <button
                    onClick={() => setEditingMaxDevices(true)}
                    className="p-1 hover:bg-gray-100 rounded transition-colors"
                    title="Edit max devices"
                  >
                    <Pencil className="w-3.5 h-3.5 text-gray-400 hover:text-gray-600" />
                  </button>
                </div>
              )}
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">Connection Count</label>
              <p className="text-gray-900">{user.connectionCount}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">Last Connection</label>
              <p className="text-gray-900">
                {user.lastConnection ? formatDate(user.lastConnection, dateSettings) : 'Never'}
              </p>
            </div>
          </div>
        </div>

        <div className="card">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Timestamps</h3>
          <div className="space-y-3">
            <div>
              <label className="text-sm font-medium text-gray-500">Created</label>
              <p className="text-gray-900">{formatDate(user.createdAt, dateSettings)}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">Last Updated</label>
              <p className="text-gray-900">{user.updatedAt ? formatDate(user.updatedAt, dateSettings) : 'N/A'}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

interface DevicesTabProps {
  user: VPNUser;
  dateSettings: DateFormatSettings;
  navigate: (path: string) => void;
}

interface DeviceAction {
  type: 'revoke' | 'reenable' | 'remove';
  deviceId: string;
  deviceName: string;
}

const DevicesTab = ({ user, dateSettings, navigate }: DevicesTabProps) => {
  const toast = useToast();
  const [devices, setDevices] = useState<AppDevice[]>([]);
  const [devicesLoading, setDevicesLoading] = useState(true);
  const [devicesError, setDevicesError] = useState<string | null>(null);
  const [pendingAction, setPendingAction] = useState<DeviceAction | null>(null);
  const [actionLoading, setActionLoading] = useState(false);

  const fetchDevices = useCallback(async () => {
    if (!user.email) {
      setDevicesLoading(false);
      return;
    }
    try {
      setDevicesLoading(true);
      // Filter by owner_email only — it's the per-user key that's kept current on
      // every (re)registration. owner_user_id is NOT reliable for this: legacy rows
      // can carry a drifted owner_user_id shared across users (a physical device
      // re-registered by different accounts), so matching on it over-shows.
      const result = await appDeviceService.listAllDevices({ ownerEmail: user.email, tenantId: user.tenantId, limit: 100 });
      setDevices(result.items || []);
    } catch (err: unknown) {
      setDevicesError(errText(err, 'Failed to load devices'));
    } finally {
      setDevicesLoading(false);
    }
  }, [user.email, user.tenantId]);

  useEffect(() => {
    fetchDevices();
  }, [fetchDevices]);

  const handleConfirmAction = async () => {
    if (!pendingAction) return;
    setActionLoading(true);
    try {
      if (pendingAction.type === 'revoke') {
        await api.post(`/api/v2/wireguard/apps/devices/${pendingAction.deviceId}/revoke`);
        toast.success('Device Revoked', `VPN access revoked for ${pendingAction.deviceName}`);
      } else if (pendingAction.type === 'reenable') {
        await api.post(`/api/v2/wireguard/apps/devices/${pendingAction.deviceId}/reenable`);
        toast.success('Device Re-enabled', `VPN access restored for ${pendingAction.deviceName}`);
      } else if (pendingAction.type === 'remove') {
        await api.delete(`/api/v2/wireguard/apps/devices/${pendingAction.deviceId}`);
        toast.success('Device Removed', `${pendingAction.deviceName} has been removed`);
      }
      setPendingAction(null);
      await fetchDevices();
    } catch (err: unknown) {
      toast.error('Action Failed', responseMessage(err) || errText(err, 'Operation failed'));
    } finally {
      setActionLoading(false);
    }
  };

  const confirmDialogConfig = pendingAction
    ? pendingAction.type === 'revoke'
      ? {
          title: 'Revoke Device Access',
          message: `Revoke VPN access for "${pendingAction.deviceName}"? The device will no longer be able to connect.`,
          confirmText: 'Revoke',
          variant: 'danger' as const,
        }
      : pendingAction.type === 'reenable'
      ? {
          title: 'Re-enable Device',
          message: `Re-enable VPN access for "${pendingAction.deviceName}"? The VPN app restores the session automatically within a minute — the user just needs to tap Connect.`,
          confirmText: 'Re-enable',
          variant: 'info' as const,
        }
      : {
          title: 'Remove Device',
          message: `Permanently remove "${pendingAction.deviceName}"? This cannot be undone.`,
          confirmText: 'Remove',
          variant: 'danger' as const,
        }
    : null;

  const deviceColumns: Column<AppDevice>[] = [
    {
      key: 'name',
      label: 'Device Name',
      sortable: true,
      render: (d) => (
        <button
          onClick={() => navigate(`/app-devices/${d.id}`)}
          className="flex items-center space-x-2 text-left hover:text-primary-600 transition-colors"
        >
          <Smartphone className="w-4 h-4 text-primary-600" />
          <span className="font-medium">{d.name || 'Unnamed Device'}</span>
        </button>
      ),
    },
    {
      key: 'platform',
      label: 'Platform',
      sortable: true,
      render: (d) => <span className="text-sm">{d.platform}</span>,
    },
    {
      key: 'peer',
      label: 'Assigned IP',
      render: (d) => (
        <span className="text-sm font-mono text-gray-600">
          {d.peer?.assignedIp || '-'}
        </span>
      ),
    },
    {
      key: 'lastSeenAt',
      label: 'Last Seen',
      render: (d) => (
        <div className="flex items-center space-x-1">
          {d.lastSeenAt ? (
            <>
              <Wifi className="w-3 h-3 text-success-500" />
              <span className="text-sm text-gray-600">
                {formatDate(d.lastSeenAt, dateSettings)}
              </span>
            </>
          ) : (
            <>
              <WifiOff className="w-3 h-3 text-gray-400" />
              <span className="text-sm text-gray-400">Never</span>
            </>
          )}
        </div>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      render: (d) => (
        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
          d.status === 'active'
            ? 'bg-success-100 text-success-700'
            : d.status === 'revoked'
            ? 'bg-red-100 text-red-700'
            : 'bg-gray-100 text-gray-700'
        }`}>
          {d.status}
        </span>
      ),
    },
    {
      key: 'id',
      label: 'Actions',
      render: (d) => (
        <div className="flex items-center space-x-2">
          {d.status !== 'revoked' ? (
            <button
              onClick={() => setPendingAction({ type: 'revoke', deviceId: d.id, deviceName: d.name || 'Unnamed Device' })}
              className="text-xs px-2 py-1 rounded bg-red-50 text-red-700 hover:bg-red-100 transition-colors"
            >
              Revoke
            </button>
          ) : (
            <button
              onClick={() => setPendingAction({ type: 'reenable', deviceId: d.id, deviceName: d.name || 'Unnamed Device' })}
              className="text-xs px-2 py-1 rounded bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors"
            >
              Re-enable
            </button>
          )}
          <button
            onClick={() => setPendingAction({ type: 'remove', deviceId: d.id, deviceName: d.name || 'Unnamed Device' })}
            className="text-xs px-2 py-1 rounded bg-gray-50 text-gray-700 hover:bg-gray-100 transition-colors"
          >
            Remove
          </button>
        </div>
      ),
    },
  ];

  if (!user.keycloakSub) {
    return (
      <div className="text-center py-12">
        <Smartphone className="mx-auto h-12 w-12 text-gray-400" />
        <p className="mt-2 text-gray-500">User has not registered yet. No devices available.</p>
      </div>
    );
  }

  if (devicesError) {
    return (
      <div className="flex items-center space-x-2 p-4 bg-red-50 rounded-lg text-red-700">
        <AlertCircle className="w-5 h-5" />
        <span>{devicesError}</span>
      </div>
    );
  }

  return (
    <>
      <DataTable
        columns={deviceColumns}
        data={devices}
        searchPlaceholder="Search devices..."
        loading={devicesLoading}
        emptyMessage="No devices registered for this user"
      />

      {pendingAction && confirmDialogConfig && (
        <ConfirmDialog
          isOpen={true}
          onClose={() => setPendingAction(null)}
          onConfirm={handleConfirmAction}
          title={confirmDialogConfig.title}
          message={confirmDialogConfig.message}
          confirmText={confirmDialogConfig.confirmText}
          cancelText="Cancel"
          variant={confirmDialogConfig.variant}
          isLoading={actionLoading}
        />
      )}
    </>
  );
};

/**
 * Secure Access (ZTNA) devices enrolled by this user (VSNP-1163).
 *
 * The seat indicator and the list come from ONE read (`/access/users/:id/devices`)
 * that runs the same count the enrolment quota check does, so "3 / 3" here means
 * the user's next enrolment WILL be rejected — and revoking or deleting a row
 * here is exactly how an admin frees the seat. Quota is a reject, never an
 * auto-replace: a compromised credential must not be able to displace a
 * legitimate device silently.
 */
const AccessDevicesTab = ({ user }: { user: VPNUser }) => {
  const toast = useToast();
  const [items, setItems] = useState<AccessDevice[]>([]);
  const [quota, setQuota] = useState<DeviceQuota | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [approveTarget, setApproveTarget] = useState<AccessDevice | null>(null);
  const [approving, setApproving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<AccessDevice | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [revokeTarget, setRevokeTarget] = useState<AccessDevice | null>(null);
  const [detailTarget, setDetailTarget] = useState<AccessDevice | null>(null);

  const fetchDevices = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await accessService.listUserDevices(user.id);
      setItems(result.items ?? []);
      setQuota(result.quota);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load Access devices');
    } finally {
      setLoading(false);
    }
  }, [user.id]);

  useEffect(() => {
    void fetchDevices();
  }, [fetchDevices]);

  const handleApprove = async () => {
    if (!approveTarget) return;
    try {
      setApproving(true);
      await accessService.approveDevice(approveTarget.id, user.tenantId);
      toast.success('Device approved', `${approveTarget.device_id} — the roster republishes automatically`);
      setApproveTarget(null);
      await fetchDevices();
    } catch (err) {
      toast.error('Approve failed', err instanceof Error ? err.message : 'Could not approve the device');
    } finally {
      setApproving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      setDeleting(true);
      await accessService.deleteDevice(deleteTarget.id, user.tenantId);
      toast.success('Device deleted', `${deleteTarget.device_id} — seat freed`);
      setDeleteTarget(null);
      await fetchDevices();
    } catch (err) {
      toast.error('Delete failed', err instanceof Error ? err.message : 'Could not delete the device');
    } finally {
      setDeleting(false);
    }
  };

  const atQuota = !!quota && !quota.unlimited && quota.used >= (quota.maxDevices ?? 0);

  const columns: Column<AccessDevice>[] = [
    {
      key: 'device_id',
      label: 'Device',
      sortable: true,
      render: (d) => (
        <button type="button" className="text-left" onClick={() => setDetailTarget(d)} title="View device detail">
          <div className="font-medium text-gray-900 hover:text-primary-600">{d.device_name || d.device_id}</div>
          <div className="text-xs font-mono text-gray-500">{d.device_id}</div>
        </button>
      ),
    },
    { key: 'platform', label: 'Platform', sortable: true },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      render: (d) => <StatusBadge status={deviceStatusLabel(d.status)} />,
    },
    {
      key: 'posture',
      label: 'Posture',
      render: (d) => {
        const facts = postureFacts(d.posture);
        return facts.length === 0 ? (
          <span className="inline-flex items-center gap-1 text-xs text-gray-400" title="No verified posture yet">
            <ShieldAlert className="w-3.5 h-3.5" /> none
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 text-xs text-teal-700" title={facts.map(([k, v]) => `${k}=${v}`).join('\n')}>
            <ShieldCheck className="w-3.5 h-3.5" /> {facts.length} fact{facts.length !== 1 ? 's' : ''}
          </span>
        );
      },
    },
    {
      key: 'cert',
      label: 'Certificate',
      render: (d) =>
        d.certificate_serial_number ? (
          <div className="text-xs">
            <div className="font-mono text-gray-600">{d.certificate_serial_number.slice(0, 12)}…</div>
            <div className="text-gray-400">exp {fmtDate(d.certificate_expires_at)}</div>
          </div>
        ) : (
          <span className="text-gray-400 text-xs">not issued</span>
        ),
    },
    { key: 'last_seen', label: 'Last Seen', sortable: true, render: (d) => <span className="text-sm text-gray-600">{fmtDate(d.last_seen)}</span> },
    {
      key: 'actions',
      label: '',
      render: (d) => (
        <div className="flex items-center justify-end gap-1">
          {d.status === 'pending_approval' && (
            <button type="button" className="p-1.5 rounded hover:bg-success-50 text-success-600" title="Approve device" onClick={() => setApproveTarget(d)}>
              <CheckCircle className="w-4 h-4" />
            </button>
          )}
          {d.status === 'active' && (
            <button type="button" className="p-1.5 rounded hover:bg-danger-50 text-danger-600" title="Revoke device (frees the seat)" onClick={() => setRevokeTarget(d)}>
              <XCircle className="w-4 h-4" />
            </button>
          )}
          <button type="button" className="p-1.5 rounded hover:bg-gray-100 text-gray-400 hover:text-danger-600" title="Delete device record (frees the seat)" onClick={() => setDeleteTarget(d)}>
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      ),
    },
  ];

  if (error) {
    return (
      <div className="flex items-center space-x-2 p-4 bg-red-50 rounded-lg text-red-700">
        <AlertCircle className="w-5 h-5" />
        <span>{error}</span>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Doc 28 §4.10: this user's own sign-in lifetime for Secure Access. */}
      <UserSignInPolicyCard userId={user.id} />
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <ZtnaChip />
          <span>Devices enrolled with the Access client. Approving admits the device; revoke or delete frees one of the user&apos;s seats.</span>
        </div>
        <button type="button" className="btn-secondary text-sm" onClick={() => void fetchDevices()} disabled={loading}>
          <RefreshCw className={`w-4 h-4 mr-2 inline ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {quota && (
        <div
          className={`flex items-center justify-between rounded-md border px-4 py-3 text-sm ${
            atQuota ? 'border-warning-200 bg-warning-50 text-warning-800' : 'border-gray-200 bg-gray-50 text-gray-700'
          }`}
        >
          <div>
            <span className="font-medium">Device seats:</span>{' '}
            {quota.unlimited ? (
              <span>{quota.used} in use · unlimited</span>
            ) : (
              <span>
                {quota.used} / {quota.maxDevices} in use
              </span>
            )}
            <span className="ml-2 text-xs text-gray-500">(pending + active count; revoked and deleted devices do not)</span>
          </div>
          {atQuota && (
            <span className="text-xs font-medium">
              Quota reached — the user&apos;s next enrolment is rejected until a device is revoked or deleted, or Max Devices is raised on Overview.
            </span>
          )}
        </div>
      )}

      <DataTable
        columns={columns}
        data={items}
        loading={loading}
        searchPlaceholder="Search Access devices…"
        emptyMessage="No Access devices enrolled by this user"
      />

      <ConfirmDialog
        isOpen={!!approveTarget}
        onClose={() => setApproveTarget(null)}
        onConfirm={handleApprove}
        title="Approve device"
        message={
          <span>
            Approve <strong>{approveTarget?.device_name || approveTarget?.device_id}</strong>? Its certificate becomes
            admissible and the signed roster republishes automatically.
          </span>
        }
        confirmText="Approve"
        variant="info"
        isLoading={approving}
      />

      <ConfirmDialog
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title="Delete device record"
        message={
          <span>
            Delete <strong>{deleteTarget?.device_id}</strong>? This removes the enrolment record and frees the seat. To
            immediately cut a connected device, use Revoke instead — it drops live sessions fleet-wide.
          </span>
        }
        confirmText="Delete"
        variant="danger"
        isLoading={deleting}
      />

      <RevokeDeviceModal
        device={revokeTarget}
        tenantId={user.tenantId}
        onClose={() => setRevokeTarget(null)}
        onDone={async () => {
          setRevokeTarget(null);
          await fetchDevices();
        }}
      />

      <DeviceDetailModal device={detailTarget} onClose={() => setDetailTarget(null)} />
    </div>
  );
};

const ActivityTab = ({ userId, dateSettings }: { userId: string; dateSettings: DateFormatSettings }) => {
  const [activities, setActivities] = useState<ActivityRecord[]>([]);
  const [activitiesLoading, setActivitiesLoading] = useState(true);
  const [activityTotal, setActivityTotal] = useState(0);
  const [activityError, setActivityError] = useState<string | null>(null);

  useEffect(() => {
    const fetchActivities = async () => {
      try {
        setActivitiesLoading(true);
        const result = await activityService.getByUser(userId, { page: 1, pageSize: 20 });
        setActivities(result.items || []);
        setActivityTotal(result.pagination?.total || 0);
      } catch (err: unknown) {
        setActivityError(errText(err, 'Failed to load activity log'));
        setActivities([]);
      } finally {
        setActivitiesLoading(false);
      }
    };
    fetchActivities();
  }, [userId]);

  const actionColors: Record<string, string> = {
    create: 'bg-green-100 text-green-700',
    update: 'bg-blue-100 text-blue-700',
    delete: 'bg-red-100 text-red-700',
    login: 'bg-purple-100 text-purple-700',
    logout: 'bg-gray-100 text-gray-700',
  };

  const activityColumns: Column<ActivityRecord>[] = [
    {
      key: 'created_at', label: 'Timestamp', sortable: true,
      render: (a) => <span className="text-sm">{a.created_at ? formatDate(a.created_at, dateSettings) : '-'}</span>,
    },
    {
      key: 'action', label: 'Action', sortable: true,
      render: (a) => <span className={`px-2 py-1 text-xs font-medium rounded-full ${actionColors[a.action] || 'bg-gray-100 text-gray-700'}`}>{a.action}</span>,
    },
    { key: 'resource_type', label: 'Resource Type', render: (a) => <span className="text-sm">{a.resource_type}</span> },
    {
      key: 'resource_id', label: 'Resource',
      render: (a) => <span className="text-sm text-gray-600">{a.resource_name || a.resource_id || '-'}</span>,
    },
    {
      key: 'status', label: 'Status', sortable: true,
      render: (a) => a.status === 'success' ? (
        <span className="inline-flex items-center space-x-1 text-success-700"><CheckCircle className="w-4 h-4" /><span>Success</span></span>
      ) : (
        <span className="inline-flex items-center space-x-1 text-red-600"><XCircle className="w-4 h-4" /><span>Failed</span></span>
      ),
    },
  ];

  if (activityError) {
    return (
      <div className="flex items-center space-x-2 p-4 bg-red-50 rounded-lg text-red-700">
        <AlertCircle className="w-5 h-5" />
        <span>{activityError}</span>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-500">{activityTotal} total events</p>
      <DataTable
        columns={activityColumns}
        data={activities}
        searchPlaceholder="Search activity..."
        loading={activitiesLoading}
      />
    </div>
  );
};

// --- Main component ---

const VPNUserDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState<VPNUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { getDateFormatSettings } = useSettingsStore();
  const dateSettings = getDateFormatSettings();

  const fetchUser = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      const data = await vpnUserService.getById(id);
      setUser(data);
    } catch (err: unknown) {
      setError(errText(err, 'Failed to load VPN user details'));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  if (loading) {
    return <div className="flex items-center justify-center h-64">Loading VPN user details...</div>;
  }

  if (error || !user) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-red-600">{error || 'VPN user not found'}</div>
      </div>
    );
  }

  const statusLabel = user.status === 0 ? 'Active' : 'Disabled';

  const tabs: Tab[] = [
    { id: 'overview', label: 'Overview', icon: Users, content: <OverviewTab user={user} dateSettings={dateSettings} onRefreshUser={fetchUser} /> },
    { id: 'devices', label: 'VPN Devices', icon: Smartphone, content: <DevicesTab user={user} dateSettings={dateSettings} navigate={navigate} /> },
    { id: 'access-devices', label: 'Access Devices', icon: ShieldCheck, content: <AccessDevicesTab user={user} /> },
    { id: 'activity', label: 'Activity', icon: Activity, content: <ActivityTab userId={id!} dateSettings={dateSettings} /> },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">VPN User: {user.email}</h1>
            <p className="text-gray-600 mt-1">{user.displayName || 'No display name'}</p>
          </div>
          <StatusBadge status={statusLabel} />
        </div>
        <button onClick={fetchUser} className="btn-secondary inline-flex items-center space-x-2">
          <RefreshCw className="w-4 h-4" />
          <span>Refresh</span>
        </button>
      </div>

      {/* Tabs */}
      <div className="card">
        <Tabs tabs={tabs} />
      </div>
    </div>
  );
};

export default VPNUserDetail;
